import { useState, useEffect } from 'react';
import { Save, Wifi, RefreshCw, Eye, EyeOff, CheckCircle, XCircle, Loader, ExternalLink, Map, MapPin, Navigation, Mail, Database, ScrollText, Clock, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { clearApiKeyCache, geocodeAddress } from '../lib/googleMaps';
import { ConfirmModal } from './ConfirmModal';
import { IntegrationLogViewer } from './IntegrationLogViewer';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';

interface AspireIntegrationRow {
  id: string;
  client_id: string;
  client_secret: string;
  is_active: boolean | null;
  last_synced_at: string | null;
}

interface AppSettingRow {
  id: string;
  key: string;
  value: string;
  description?: string | null;
}

interface SyncResult {
  success: boolean;
  message: string;
  total?: number;
  added?: number;
  updated?: number;
}

interface BackfillResult {
  success: boolean;
  message: string;
  matched?: number;
  unmatched?: number;
  unmatched_names?: string[];
}

interface GeocodeResult {
  success: boolean;
  message: string;
  total?: number;
  geocoded?: number;
  failed?: number;
}

const APP_SETTING_DESCRIPTIONS: Record<string, string> = {
  google_maps_api_key: 'Google Maps JavaScript API key used for property maps, GPS previews, and address geocoding',
  azure_email_connection_string: 'Azure Communication Services connection string used to send report emails',
  azure_email_sender_address: 'Verified sender email address from the Azure Email Communication Service domain',
  nearby_search_radius_miles: 'Search radius (miles) for nearby properties when starting an inspection',
  nearby_property_count: 'Number of nearby properties to surface at the top of the inspection start list',
  tracking_min_distance_m: 'Inspections Dashboard: metres a technician must move before a new breadcrumb point is recorded',
  tracking_min_interval_sec: 'Inspections Dashboard: max seconds between recorded breadcrumb points while a technician is stationary',
  tracking_max_accuracy_m: 'Inspections Dashboard: drop GPS breadcrumb fixes whose reported accuracy is worse than this many metres',
  aspire_auto_create_properties: 'When loading the Aspire inspection schedule, automatically create a local Property for any scheduled Aspire property that does not exist yet',
  show_water_violations_in_reports: 'Show water-restriction policy violations on controllers in printed and emailed reports (always shown in the on-screen manager review)',
  inspection_duration_enabled: 'Show the calculated time-on-site (inspection duration) on the manager review',
  inspection_duration_gap_minutes: 'Inspection duration: a gap longer than this many minutes between activity is treated as the tech leaving and returning (a separate visit)',
};

async function upsertAppSetting(allSettings: AppSettingRow[], key: string, value: string): Promise<AppSettingRow> {
  const existing = allSettings.find((row) => row.key === key);
  if (existing) {
    return apiClient.put<AppSettingRow>(`/api/app-settings/${existing.id}`, { value });
  }
  return apiClient.post<AppSettingRow>('/api/app-settings', {
    key,
    value,
    description: APP_SETTING_DESCRIPTIONS[key] ?? null,
  });
}

export function IntegrationSettings() {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [integration, setIntegration] = useState<AspireIntegrationRow | null>(null);
  const [formData, setFormData] = useState({ client_id: '', client_secret: '' });
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const [mapsApiKey, setMapsApiKey] = useState('');
  const [mapsKeyConfigured, setMapsKeyConfigured] = useState(false);
  const [showMapsKey, setShowMapsKey] = useState(false);
  const [savingMapsKey, setSavingMapsKey] = useState(false);
  const [testingMaps, setTestingMaps] = useState(false);
  const [mapsTestResult, setMapsTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<GeocodeResult | null>(null);

  const [searchRadius, setSearchRadius] = useState('50');
  const [nearbyCount, setNearbyCount] = useState('3');
  const [trackingDistance, setTrackingDistance] = useState('8');
  const [trackingInterval, setTrackingInterval] = useState('12');
  const [trackingGeofence, setTrackingGeofence] = useState('500');
  const [trackingMaxAccuracy, setTrackingMaxAccuracy] = useState('35');
  const [savingTracking, setSavingTracking] = useState(false);
  const [trackingSaved, setTrackingSaved] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationSaved, setLocationSaved] = useState(false);

  const [errorModal, setErrorModal] = useState<{ title: string; message: string; error?: unknown; action?: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const [azureConnectionString, setAzureConnectionString] = useState('');
  const [azureSender, setAzureSender] = useState('');
  const [showConnectionString, setShowConnectionString] = useState(false);
  const [azureConfigured, setAzureConfigured] = useState(false);
  const [savingAzure, setSavingAzure] = useState(false);
  const [testingAzure, setTestingAzure] = useState(false);
  const [azureTestResult, setAzureTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);

  // Which Aspire property statuses sync into the app (multi-select).
  const [aspireStatuses, setAspireStatuses] = useState<{ id: number; name: string; active: boolean }[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(['Customer']));
  const [statusesLoadError, setStatusesLoadError] = useState(false);
  const [savingStatuses, setSavingStatuses] = useState(false);
  const [statusesSaved, setStatusesSaved] = useState(false);

  // Auto-create local Properties for unlinked Aspire properties when loading the schedule.
  const [autoCreateProps, setAutoCreateProps] = useState(false);
  const [savingAutoCreate, setSavingAutoCreate] = useState(false);

  // Show water-restriction violations in printed/emailed reports (default on).
  const [showWaterViolations, setShowWaterViolations] = useState(true);
  const [savingWaterViolations, setSavingWaterViolations] = useState(false);

  // Inspection duration (time-on-site) shown on the manager review (default on).
  const [durationEnabled, setDurationEnabled] = useState(true);
  const [savingDurationEnabled, setSavingDurationEnabled] = useState(false);
  const [durationGapMinutes, setDurationGapMinutes] = useState('15');
  const [savingDurationGap, setSavingDurationGap] = useState(false);
  const [durationGapSaved, setDurationGapSaved] = useState(false);

  const [logViewer, setLogViewer] = useState<{ name: string; label: string } | null>(null);

  // App settings cache so per-section saves don't refetch the entire list each time.
  const [appSettings, setAppSettings] = useState<AppSettingRow[]>([]);

  useEffect(() => {
    void loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [aspireRows, settingsRows] = await Promise.all([
        apiClient.get<AspireIntegrationRow[]>('/api/aspire-settings'),
        apiClient.get<AppSettingRow[]>('/api/app-settings'),
      ]);

      const first = aspireRows[0] ?? null;
      if (first) {
        setIntegration(first);
        setFormData({ client_id: first.client_id, client_secret: first.client_secret });
      }

      setAppSettings(settingsRows);

      const mapsRow = settingsRows.find((r) => r.key === 'google_maps_api_key');
      if (mapsRow?.value) {
        setMapsApiKey(mapsRow.value);
        setMapsKeyConfigured(true);
      }

      const radiusRow = settingsRows.find((r) => r.key === 'nearby_search_radius_miles');
      if (radiusRow?.value) setSearchRadius(radiusRow.value);
      const countRow = settingsRows.find((r) => r.key === 'nearby_property_count');
      if (countRow?.value) setNearbyCount(countRow.value);

      const trkDistRow = settingsRows.find((r) => r.key === 'tracking_min_distance_m');
      if (trkDistRow?.value) setTrackingDistance(trkDistRow.value);
      const trkIntervalRow = settingsRows.find((r) => r.key === 'tracking_min_interval_sec');
      if (trkIntervalRow?.value) setTrackingInterval(trkIntervalRow.value);
      const trkGeofenceRow = settingsRows.find((r) => r.key === 'tracking_geofence_radius_m');
      if (trkGeofenceRow?.value) setTrackingGeofence(trkGeofenceRow.value);
      const trkMaxAccRow = settingsRows.find((r) => r.key === 'tracking_max_accuracy_m');
      if (trkMaxAccRow?.value) setTrackingMaxAccuracy(trkMaxAccRow.value);

      const autoCreateRow = settingsRows.find((r) => r.key === 'aspire_auto_create_properties');
      setAutoCreateProps(autoCreateRow?.value === 'true');

      // Default ON: only an explicit 'false' hides report violations.
      const waterViolRow = settingsRows.find((r) => r.key === 'show_water_violations_in_reports');
      setShowWaterViolations(waterViolRow?.value !== 'false');

      const durEnabledRow = settingsRows.find((r) => r.key === 'inspection_duration_enabled');
      setDurationEnabled(durEnabledRow?.value !== 'false');
      const durGapRow = settingsRows.find((r) => r.key === 'inspection_duration_gap_minutes');
      if (durGapRow?.value) setDurationGapMinutes(durGapRow.value);

      const aspireStatusRow = settingsRows.find((r) => r.key === 'aspire_property_statuses');
      if (aspireStatusRow?.value) {
        try {
          const arr = JSON.parse(aspireStatusRow.value);
          if (Array.isArray(arr)) {
            setSelectedStatuses(new Set(arr.filter((s: unknown): s is string => typeof s === 'string')));
          }
        } catch {
          /* keep default */
        }
      }
      // Best-effort: list Aspire's statuses for the multi-select (needs valid creds).
      apiClient
        .get<{ statuses: { id: number; name: string; active: boolean }[] }>(
          '/api/integrations/aspire/property-statuses',
        )
        .then((r) => setAspireStatuses(r.statuses ?? []))
        .catch(() => setStatusesLoadError(true));

      const azureConnRow = settingsRows.find((r) => r.key === 'azure_email_connection_string');
      const azureSenderRow = settingsRows.find((r) => r.key === 'azure_email_sender_address');
      if (azureConnRow?.value) setAzureConnectionString(azureConnRow.value);
      if (azureSenderRow?.value) setAzureSender(azureSenderRow.value);
      setAzureConfigured(!!azureConnRow?.value && !!azureSenderRow?.value);
    } catch (err) {
      console.error('Error loading integration settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const refetchAppSettings = async (): Promise<AppSettingRow[]> => {
    const rows = await apiClient.get<AppSettingRow[]>('/api/app-settings');
    setAppSettings(rows);
    return rows;
  };

  const refetchAspire = async () => {
    try {
      const rows = await apiClient.get<AspireIntegrationRow[]>('/api/aspire-settings');
      const first = rows[0] ?? null;
      if (first) {
        setIntegration(first);
        setFormData({ client_id: first.client_id, client_secret: first.client_secret });
      } else {
        setIntegration(null);
      }
    } catch (err) {
      console.error('Error reloading Aspire integration:', err);
    }
  };

  // -------------------------------------------------------------------------
  // Aspire credentials
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!formData.client_id.trim() || !formData.client_secret.trim()) {
      setErrorModal({ title: 'Missing Credentials', message: 'Please fill in both Client ID and Client Secret before saving.' });
      return;
    }

    setSaving(true);
    setTestResult(null);
    setSyncResult(null);

    try {
      const payload = {
        client_id: formData.client_id.trim(),
        client_secret: formData.client_secret.trim(),
      };
      if (integration) {
        await apiClient.put<AspireIntegrationRow>(`/api/aspire-settings/${integration.id}`, payload);
      } else {
        await apiClient.post<AspireIntegrationRow>('/api/aspire-settings', payload);
      }

      logAudit({
        action: integration ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityId: integration?.id,
        entityLabel: 'Aspire',
        details: { client_id: payload.client_id, client_secret: '***REDACTED***' },
        pageContext: 'integrations',
      });
      await refetchAspire();
    } catch (error) {
      console.error('Error saving integration:', error);
      setErrorModal({ title: 'Save Failed', message: 'Failed to save integration settings. Please try again.', error, action: 'saveAspireCredentials' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setSyncResult(null);

    try {
      const data = await apiClient.post<{ success: boolean; message?: string; error?: string }>(
        '/api/integrations/aspire/test',
      );
      if (data.success === false || data.error) {
        setTestResult({ success: false, message: data.error || 'Connection test failed' });
      } else {
        setTestResult({ success: true, message: data.message || 'Connection successful' });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Network error';
      setTestResult({ success: false, message });
    } finally {
      setTesting(false);
    }
  };

  const handleSync = () => {
    setConfirmDialog({
      title: 'Sync Work Codes from Aspire',
      message: 'This will sync work codes from Aspire. New codes will be added and existing synced codes will be updated. Continue?',
      onConfirm: () => {
        setConfirmDialog(null);
        void runSync();
      },
    });
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setTestResult(null);

    try {
      const data = await apiClient.post<SyncResult & { error?: string }>(
        '/api/integrations/aspire/sync',
      );
      if (!data.success || data.error) {
        setSyncResult({ success: false, message: data.error || 'Sync failed' });
      } else {
        setSyncResult(data);
        await refetchAspire();
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Network error';
      setSyncResult({ success: false, message });
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveStatuses = async () => {
    if (selectedStatuses.size === 0) return;
    setSavingStatuses(true);
    setStatusesSaved(false);
    try {
      await upsertAppSetting(appSettings, 'aspire_property_statuses', JSON.stringify([...selectedStatuses]));
      await refetchAppSettings();
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: 'Aspire Property Statuses',
        details: { statuses: [...selectedStatuses] },
        pageContext: 'integrations',
      });
      setStatusesSaved(true);
      setTimeout(() => setStatusesSaved(false), 3000);
    } catch (error) {
      setErrorModal({ title: 'Save Failed', message: 'Failed to save property statuses. Please try again.', error, action: 'saveAspireStatuses' });
    } finally {
      setSavingStatuses(false);
    }
  };

  const handleToggleWaterViolations = async (next: boolean) => {
    setSavingWaterViolations(true);
    const prev = showWaterViolations;
    setShowWaterViolations(next); // optimistic
    try {
      await upsertAppSetting(appSettings, 'show_water_violations_in_reports', next ? 'true' : 'false');
      await refetchAppSettings();
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: `Show Water Violations in Reports: ${next ? 'on' : 'off'}`,
        pageContext: 'integrations',
      });
    } catch (error) {
      setShowWaterViolations(prev); // revert on failure
      setErrorModal({ title: 'Save Failed', message: 'Failed to save the report setting. Please try again.', error, action: 'saveWaterViolations' });
    } finally {
      setSavingWaterViolations(false);
    }
  };

  const handleToggleDurationEnabled = async (next: boolean) => {
    setSavingDurationEnabled(true);
    const prev = durationEnabled;
    setDurationEnabled(next); // optimistic
    try {
      await upsertAppSetting(appSettings, 'inspection_duration_enabled', next ? 'true' : 'false');
      await refetchAppSettings();
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: `Inspection Duration: ${next ? 'on' : 'off'}`,
        pageContext: 'integrations',
      });
    } catch (error) {
      setDurationEnabled(prev);
      setErrorModal({ title: 'Save Failed', message: 'Failed to save the duration setting. Please try again.', error, action: 'saveDurationEnabled' });
    } finally {
      setSavingDurationEnabled(false);
    }
  };

  const handleSaveDurationGap = async () => {
    const n = parseInt(durationGapMinutes, 10);
    if (!Number.isFinite(n) || n < 1 || n > 240) {
      setErrorModal({ title: 'Invalid Value', message: 'Visit-gap must be a whole number of minutes between 1 and 240.', action: 'saveDurationGap' });
      return;
    }
    setSavingDurationGap(true);
    setDurationGapSaved(false);
    try {
      await upsertAppSetting(appSettings, 'inspection_duration_gap_minutes', String(n));
      await refetchAppSettings();
      setDurationGapMinutes(String(n));
      setDurationGapSaved(true);
      setTimeout(() => setDurationGapSaved(false), 2500);
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: `Inspection Duration Visit-Gap: ${n} min`,
        pageContext: 'integrations',
      });
    } catch (error) {
      setErrorModal({ title: 'Save Failed', message: 'Failed to save the visit-gap. Please try again.', error, action: 'saveDurationGap' });
    } finally {
      setSavingDurationGap(false);
    }
  };

  const handleToggleAutoCreate = async (next: boolean) => {
    setSavingAutoCreate(true);
    const prev = autoCreateProps;
    setAutoCreateProps(next); // optimistic
    try {
      await upsertAppSetting(appSettings, 'aspire_auto_create_properties', next ? 'true' : 'false');
      await refetchAppSettings();
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: 'Aspire Auto-create Properties',
        details: { enabled: next },
        pageContext: 'integrations',
      });
    } catch (error) {
      setAutoCreateProps(prev); // revert on failure
      setErrorModal({ title: 'Save Failed', message: 'Failed to save the auto-create setting. Please try again.', error, action: 'saveAspireAutoCreate' });
    } finally {
      setSavingAutoCreate(false);
    }
  };

  const handleBackfillPropertyIds = () => {
    setConfirmDialog({
      title: 'Backfill Aspire Property IDs',
      message: 'This will match all local properties to Aspire by name and update their Aspire Property ID. Continue?',
      onConfirm: () => {
        setConfirmDialog(null);
        void runBackfillPropertyIds();
      },
    });
  };

  const runBackfillPropertyIds = async () => {
    setBackfilling(true);
    setBackfillResult(null);

    try {
      const data = await apiClient.post<BackfillResult & { error?: string }>(
        '/api/integrations/aspire/backfill',
      );
      if (!data.success || data.error) {
        setBackfillResult({ success: false, message: data.error || 'Backfill failed' });
      } else {
        setBackfillResult({
          success: true,
          message: data.message,
          matched: data.matched,
          unmatched: data.unmatched,
          unmatched_names: data.unmatched_names,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Network error';
      setBackfillResult({ success: false, message });
    } finally {
      setBackfilling(false);
    }
  };

  // -------------------------------------------------------------------------
  // Google Maps API key + location proximity
  // -------------------------------------------------------------------------
  const handleSaveMapsKey = async () => {
    if (!mapsApiKey.trim()) {
      setErrorModal({ title: 'Missing API Key', message: 'Please enter a Google Maps API key before saving.' });
      return;
    }

    setSavingMapsKey(true);
    setMapsTestResult(null);

    try {
      await upsertAppSetting(appSettings, 'google_maps_api_key', mapsApiKey.trim());
      await refetchAppSettings();

      clearApiKeyCache();
      setMapsKeyConfigured(true);
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: 'Google Maps API Key',
        details: { api_key: '***REDACTED***' },
        pageContext: 'integrations',
      });
    } catch (error) {
      console.error('Error saving Maps API key:', error);
      setErrorModal({ title: 'Save Failed', message: 'Failed to save Google Maps API key. Please try again.', error, action: 'saveMapsKey' });
    } finally {
      setSavingMapsKey(false);
    }
  };

  const handleTestMaps = async () => {
    setTestingMaps(true);
    setMapsTestResult(null);

    try {
      clearApiKeyCache();
      const result = await geocodeAddress('New York, NY');

      if (result) {
        setMapsTestResult({
          success: true,
          message: `Connection successful. Test geocode returned ${result.latitude.toFixed(4)}, ${result.longitude.toFixed(4)}`,
        });
      } else {
        setMapsTestResult({
          success: false,
          message: 'Geocode returned no results. Verify the API key has Geocoding API enabled.',
        });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to load Google Maps. Check the API key.';
      setMapsTestResult({ success: false, message: msg });
    } finally {
      setTestingMaps(false);
    }
  };

  const handleGeocodeProperties = () => {
    setConfirmDialog({
      title: 'Geocode Properties',
      message: 'This will geocode all properties that are missing GPS coordinates using their address. This may take a few minutes. Continue?',
      onConfirm: () => {
        setConfirmDialog(null);
        void runGeocodeProperties();
      },
    });
  };

  const runGeocodeProperties = async () => {
    setGeocoding(true);
    setGeocodeResult(null);
    setMapsTestResult(null);

    try {
      const data = await apiClient.post<GeocodeResult & { error?: string }>(
        '/api/integrations/geocode',
      );
      if (!data.success || data.error) {
        setGeocodeResult({ success: false, message: data.error || 'Geocoding failed' });
      } else {
        setGeocodeResult({
          success: true,
          message: data.message,
          total: data.total,
          geocoded: data.geocoded,
          failed: data.failed,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Network error';
      setGeocodeResult({ success: false, message });
    } finally {
      setGeocoding(false);
    }
  };

  const handleSaveLocationSettings = async () => {
    const radius = parseInt(searchRadius, 10);
    const count = parseInt(nearbyCount, 10);
    if (isNaN(radius) || radius < 1 || radius > 2000) {
      setErrorModal({ title: 'Invalid Search Radius', message: 'Search radius must be between 1 and 2,000 miles.' });
      return;
    }
    if (isNaN(count) || count < 1 || count > 20) {
      setErrorModal({ title: 'Invalid Property Count', message: 'Nearby property count must be between 1 and 20.' });
      return;
    }

    setSavingLocation(true);
    setLocationSaved(false);

    try {
      let current = appSettings;
      await upsertAppSetting(current, 'nearby_search_radius_miles', String(radius));
      current = await refetchAppSettings();
      await upsertAppSetting(current, 'nearby_property_count', String(count));
      await refetchAppSettings();

      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: 'Location Proximity Settings',
        details: { search_radius: radius, nearby_count: count },
        pageContext: 'integrations',
      });
      setLocationSaved(true);
      setTimeout(() => setLocationSaved(false), 3000);
    } catch (error) {
      console.error('Error saving location settings:', error);
      setErrorModal({ title: 'Save Failed', message: 'Failed to save location settings. Please try again.', error, action: 'saveLocationSettings' });
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSaveTrackingSettings = async () => {
    const dist = parseInt(trackingDistance, 10);
    const interval = parseInt(trackingInterval, 10);
    const geofence = parseInt(trackingGeofence, 10);
    const maxAcc = parseInt(trackingMaxAccuracy, 10);
    if (isNaN(dist) || dist < 1 || dist > 500) {
      setErrorModal({ title: 'Invalid Distance', message: 'Minimum distance must be between 1 and 500 meters.' });
      return;
    }
    if (isNaN(interval) || interval < 2 || interval > 600) {
      setErrorModal({ title: 'Invalid Interval', message: 'Minimum interval must be between 2 and 600 seconds.' });
      return;
    }
    if (isNaN(geofence) || geofence < 50 || geofence > 5000) {
      setErrorModal({ title: 'Invalid Radius', message: 'Leave-property radius must be between 50 and 5000 meters.' });
      return;
    }
    if (isNaN(maxAcc) || maxAcc < 10 || maxAcc > 200) {
      setErrorModal({ title: 'Invalid Accuracy', message: 'Maximum accuracy must be between 10 and 200 meters.' });
      return;
    }

    setSavingTracking(true);
    setTrackingSaved(false);

    try {
      let current = appSettings;
      await upsertAppSetting(current, 'tracking_min_distance_m', String(dist));
      current = await refetchAppSettings();
      await upsertAppSetting(current, 'tracking_min_interval_sec', String(interval));
      current = await refetchAppSettings();
      await upsertAppSetting(current, 'tracking_geofence_radius_m', String(geofence));
      current = await refetchAppSettings();
      await upsertAppSetting(current, 'tracking_max_accuracy_m', String(maxAcc));
      await refetchAppSettings();

      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: 'Live Tracking Settings',
        details: { min_distance_m: dist, min_interval_sec: interval, geofence_radius_m: geofence, max_accuracy_m: maxAcc },
        pageContext: 'integrations',
      });
      setTrackingSaved(true);
      setTimeout(() => setTrackingSaved(false), 3000);
    } catch (error) {
      console.error('Error saving tracking settings:', error);
      setErrorModal({ title: 'Save Failed', message: 'Failed to save tracking settings. Please try again.', error, action: 'saveTrackingSettings' });
    } finally {
      setSavingTracking(false);
    }
  };

  // -------------------------------------------------------------------------
  // Azure email
  // -------------------------------------------------------------------------
  const handleSaveAzure = async () => {
    if (!azureConnectionString.trim()) {
      setErrorModal({ title: 'Missing Connection String', message: 'Please enter the Azure Communication Services connection string.' });
      return;
    }
    if (!azureSender.trim()) {
      setErrorModal({ title: 'Missing Sender', message: 'Please enter a verified sender email address.' });
      return;
    }

    setSavingAzure(true);
    setAzureTestResult(null);

    try {
      let current = appSettings;
      await upsertAppSetting(current, 'azure_email_connection_string', azureConnectionString.trim());
      current = await refetchAppSettings();
      await upsertAppSetting(current, 'azure_email_sender_address', azureSender.trim());
      await refetchAppSettings();

      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INTEGRATION,
        entityLabel: 'Azure Email',
        details: { sender_address: azureSender.trim(), connection_string: '***REDACTED***' },
        pageContext: 'integrations',
      });
      setAzureConfigured(!!(azureConnectionString.trim() && azureSender.trim()));
    } catch (error) {
      console.error('Error saving Azure settings:', error);
      setErrorModal({ title: 'Save Failed', message: 'Failed to save Azure Email settings. Please try again.', error, action: 'saveAzureSettings' });
    } finally {
      setSavingAzure(false);
    }
  };

  const handleTestAzure = async () => {
    setTestingAzure(true);
    setAzureTestResult(null);

    try {
      const data = await apiClient.post<{ success: boolean; message?: string; error?: string }>(
        '/api/integrations/email/test',
      );
      if (data.success && data.message) {
        setAzureTestResult({ success: true, message: data.message });
      } else {
        setAzureTestResult({ success: false, message: data.error || 'Test failed' });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Test failed';
      setAzureTestResult({ success: false, message });
    } finally {
      setTestingAzure(false);
    }
  };

  const hasCredentials = !!(integration && integration.client_id && integration.client_secret);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-theme-secondary">Loading integration settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-theme-primary mb-1">Integrations</h2>
        <p className="text-sm text-theme-secondary">
          Connect external platforms to sync data automatically
        </p>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="px-6 py-4 border-b border-line bg-subtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center">
                <ExternalLink size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-primary">Aspire</h3>
                <p className="text-xs text-theme-secondary">Field Services Management Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasCredentials && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {t('integrations.configured', 'Configured')}
                </span>
              )}
              <button
                onClick={() => setLogViewer({ name: 'aspire', label: 'Aspire' })}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 transition-colors"
              >
                <ScrollText size={12} />
                {t('integrations.viewLogs', 'View Logs')}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-theme-primary mb-1.5">
                Client ID
              </label>
              <input
                type="text"
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                placeholder="Enter your Aspire Client ID"
                className="w-full px-3 py-2.5 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-primary mb-1.5">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={formData.client_secret}
                  onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  placeholder="Enter your Aspire Client Secret"
                  className="w-full px-3 py-2.5 pr-10 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-theme-secondary hover:text-theme-primary"
                >
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Credentials'}
            </button>

            <button
              onClick={handleTest}
              disabled={testing || !hasCredentials}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {testing ? <Loader size={16} className="animate-spin" /> : <Wifi size={16} />}
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            <button
              onClick={handleSync}
              disabled={syncing || !hasCredentials}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {syncing ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              {syncing ? 'Syncing...' : 'Sync Work Codes'}
            </button>

            <button
              onClick={handleBackfillPropertyIds}
              disabled={backfilling || !hasCredentials}
              className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {backfilling ? <Loader size={16} className="animate-spin" /> : <Database size={16} />}
              {backfilling ? 'Backfilling...' : 'Backfill Property IDs'}
            </button>
          </div>

          {testResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              testResult.success
                ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30'
                : 'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
            }`}>
              {testResult.success
                ? <CheckCircle size={20} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                : <XCircle size={20} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-sm font-medium ${testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </p>
                <p className={`text-xs mt-0.5 ${testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {testResult.message}
                </p>
              </div>
            </div>
          )}

          {syncResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              syncResult.success
                ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30'
                : 'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
            }`}>
              {syncResult.success
                ? <CheckCircle size={20} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                : <XCircle size={20} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-sm font-medium ${syncResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {syncResult.success ? 'Sync Completed' : 'Sync Failed'}
                </p>
                <p className={`text-xs mt-0.5 ${syncResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {syncResult.message}
                </p>
                {syncResult.success && syncResult.total !== undefined && (
                  <div className="flex gap-4 mt-2">
                    <span className="text-xs text-green-700 dark:text-green-300">
                      Total from Aspire: <strong>{syncResult.total}</strong>
                    </span>
                    <span className="text-xs text-green-700 dark:text-green-300">
                      New: <strong>{syncResult.added}</strong>
                    </span>
                    <span className="text-xs text-green-700 dark:text-green-300">
                      Updated: <strong>{syncResult.updated}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {backfillResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              backfillResult.success
                ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30'
                : 'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
            }`}>
              {backfillResult.success
                ? <CheckCircle size={20} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                : <XCircle size={20} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-sm font-medium ${backfillResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {backfillResult.success ? 'Backfill Complete' : 'Backfill Failed'}
                </p>
                <p className={`text-xs mt-0.5 ${backfillResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {backfillResult.message}
                </p>
                {backfillResult.success && backfillResult.matched !== undefined && (
                  <div className="flex gap-4 mt-2">
                    <span className="text-xs text-green-700 dark:text-green-300">
                      Matched: <strong>{backfillResult.matched}</strong>
                    </span>
                    <span className="text-xs text-amber-700 dark:text-amber-300">
                      Unmatched: <strong>{backfillResult.unmatched}</strong>
                    </span>
                  </div>
                )}
                {backfillResult.unmatched_names && backfillResult.unmatched_names.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-amber-700 dark:text-amber-300 cursor-pointer">
                      View unmatched properties ({backfillResult.unmatched_names.length})
                    </summary>
                    <ul className="mt-1 text-xs text-amber-800 dark:text-amber-300 max-h-40 overflow-y-auto space-y-0.5">
                      {backfillResult.unmatched_names.map((name, i) => (
                        <li key={i} className="pl-2 border-l-2 border-amber-300 dark:border-amber-500/30">{name}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}

          {integration?.last_synced_at && (
            <p className="text-xs text-theme-secondary">
              Last synced: {new Date(integration.last_synced_at).toLocaleString()}
            </p>
          )}

          {/* Which Aspire property statuses sync into the app */}
          <div className="border-t border-line pt-4">
            <h4 className="text-sm font-semibold text-theme-primary">Property Statuses to Sync</h4>
            <p className="text-xs text-theme-secondary mb-3">
              Only Aspire properties whose status is selected here appear in the inspection property
              search. Select one or more. (Defaults to "Customer".)
            </p>
            {statusesLoadError ? (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Couldn't load statuses from Aspire — save valid credentials and Test Connection first.
              </p>
            ) : aspireStatuses.length === 0 ? (
              <p className="text-xs text-theme-secondary">Loading statuses…</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {aspireStatuses.map((s) => {
                    const on = selectedStatuses.has(s.name);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm ${
                          on ? 'bg-blue-50 border-blue-300 text-blue-800 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-200' : 'bg-surface border-line text-theme-primary'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() =>
                            setSelectedStatuses((prev) => {
                              const next = new Set(prev);
                              if (next.has(s.name)) next.delete(s.name);
                              else next.add(s.name);
                              return next;
                            })
                          }
                          className="rounded"
                        />
                        {s.name}
                      </label>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={handleSaveStatuses}
                    disabled={savingStatuses || selectedStatuses.size === 0}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {savingStatuses ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                    {savingStatuses ? 'Saving...' : 'Save Statuses'}
                  </button>
                  {statusesSaved && (
                    <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 font-medium">
                      <CheckCircle size={14} />
                      Saved
                    </span>
                  )}
                  {selectedStatuses.size === 0 && (
                    <span className="text-xs text-amber-600 dark:text-amber-400">Select at least one status.</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Auto-create local Properties for unlinked Aspire properties on schedule load */}
          <div className="border-t border-line pt-4">
            <h4 className="text-sm font-semibold text-theme-primary">Auto-create Properties from the Schedule</h4>
            <p className="text-xs text-theme-secondary mb-3">
              When the inspection schedule is loaded from Aspire, automatically create a local
              Property for any scheduled Aspire property that isn't in the app yet — pulling its
              name, address, location, and water-restriction city/class from Aspire so it can be
              inspected right away. Off by default.
            </p>
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                role="switch"
                aria-checked={autoCreateProps}
                disabled={savingAutoCreate}
                onClick={() => void handleToggleAutoCreate(!autoCreateProps)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                  autoCreateProps ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition-transform ${
                    autoCreateProps ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-theme-primary">{autoCreateProps ? 'Enabled' : 'Disabled'}</span>
              {savingAutoCreate && <Loader size={14} className="animate-spin text-theme-secondary" />}
            </label>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-sm font-semibold text-theme-primary mb-2">How it works</h3>
        <ol className="text-sm text-theme-secondary space-y-2 list-decimal list-inside">
          <li>Enter your Aspire API credentials (Client ID and Secret) and save them.</li>
          <li>Use <strong>Test Connection</strong> to verify your credentials work correctly.</li>
          <li>Click <strong>Sync Work Codes</strong> to pull Irrigation catalog items from Aspire into the System Work Codes list.</li>
          <li>Items are categorized by their <code className="px-1.5 py-0.5 bg-subtle rounded text-xs">ItemCode</code> field: Controller, Zone, or DoubleCheck (defaults to Zone if unspecified).</li>
        </ol>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="px-6 py-4 border-b border-line bg-subtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <Map size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-primary">Google Maps</h3>
                <p className="text-xs text-theme-secondary">Maps, geocoding, and location services</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {mapsKeyConfigured ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {t('integrations.configured', 'Configured')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-subtle text-theme-secondary">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  {t('integrations.notConfigured', 'Not Configured')}
                </span>
              )}
              <button
                onClick={() => setLogViewer({ name: 'google_maps', label: 'Google Maps' })}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 transition-colors"
              >
                <ScrollText size={12} />
                {t('integrations.viewLogs', 'View Logs')}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-theme-primary mb-1.5">
              API Key
            </label>
            <div className="relative">
              <input
                type={showMapsKey ? 'text' : 'password'}
                value={mapsApiKey}
                onChange={(e) => setMapsApiKey(e.target.value)}
                placeholder="Enter your Google Maps API key"
                className="w-full px-3 py-2.5 pr-10 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button
                type="button"
                onClick={() => setShowMapsKey(!showMapsKey)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-theme-secondary hover:text-theme-primary"
              >
                {showMapsKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-theme-secondary mt-1.5">
              Enter your Google Maps API key to enable property maps, GPS previews, and automatic address geocoding. Required APIs: Maps JavaScript API, Geocoding API.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSaveMapsKey}
              disabled={savingMapsKey}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {savingMapsKey ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              {savingMapsKey ? 'Saving...' : 'Save API Key'}
            </button>

            <button
              onClick={handleTestMaps}
              disabled={testingMaps || !mapsKeyConfigured}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {testingMaps ? <Loader size={16} className="animate-spin" /> : <Wifi size={16} />}
              {testingMaps ? 'Testing...' : 'Test Connection'}
            </button>

            <button
              onClick={handleGeocodeProperties}
              disabled={geocoding || !mapsKeyConfigured}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {geocoding ? <Loader size={16} className="animate-spin" /> : <MapPin size={16} />}
              {geocoding ? 'Geocoding...' : 'Geocode All Properties'}
            </button>
          </div>

          {mapsTestResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              mapsTestResult.success
                ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30'
                : 'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
            }`}>
              {mapsTestResult.success
                ? <CheckCircle size={20} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                : <XCircle size={20} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-sm font-medium ${mapsTestResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {mapsTestResult.success ? 'Connection Successful' : 'Connection Failed'}
                </p>
                <p className={`text-xs mt-0.5 ${mapsTestResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {mapsTestResult.message}
                </p>
              </div>
            </div>
          )}

          {geocodeResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              geocodeResult.success
                ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30'
                : 'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
            }`}>
              {geocodeResult.success
                ? <CheckCircle size={20} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                : <XCircle size={20} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-sm font-medium ${geocodeResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {geocodeResult.success ? 'Geocoding Complete' : 'Geocoding Failed'}
                </p>
                <p className={`text-xs mt-0.5 ${geocodeResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {geocodeResult.message}
                </p>
                {geocodeResult.success && geocodeResult.total !== undefined && (
                  <div className="flex gap-4 mt-2">
                    <span className="text-xs text-green-700 dark:text-green-300">
                      Properties processed: <strong>{geocodeResult.total}</strong>
                    </span>
                    <span className="text-xs text-green-700 dark:text-green-300">
                      Geocoded: <strong>{geocodeResult.geocoded}</strong>
                    </span>
                    {(geocodeResult.failed ?? 0) > 0 && (
                      <span className="text-xs text-amber-700 dark:text-amber-300">
                        Failed: <strong>{geocodeResult.failed}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border-t border-line pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Navigation size={16} className="text-blue-600" />
              <h4 className="text-sm font-semibold text-theme-primary">Location Proximity</h4>
            </div>
            <p className="text-xs text-theme-secondary mb-4">
              When a technician starts an inspection, the app uses their GPS to find nearby properties and highlight them at the top of the list. Adjust the search radius and how many properties to surface.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">
                  Search Radius
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="2000"
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(e.target.value)}
                    className="w-full px-3 py-2.5 pr-14 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-theme-secondary">miles</span>
                </div>
                <p className="text-xs text-theme-secondary mt-1">1 - 2,000 miles</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">
                  Nearby Properties Shown
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={nearbyCount}
                  onChange={(e) => setNearbyCount(e.target.value)}
                  className="w-full px-3 py-2.5 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-theme-secondary mt-1">1 - 20 properties</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleSaveLocationSettings}
                disabled={savingLocation}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {savingLocation ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                {savingLocation ? 'Saving...' : 'Save Settings'}
              </button>
              {locationSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle size={14} />
                  Saved
                </span>
              )}
            </div>
          </div>

          <div className="border-t border-line pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Navigation size={16} className="text-blue-600" />
              <h4 className="text-sm font-semibold text-theme-primary">Live Location Tracking</h4>
            </div>
            <p className="text-xs text-theme-secondary mb-4">
              Controls how the inspections map records a technician's path during an inspection. A breadcrumb point is saved once they move at least the minimum distance, or at least this often while standing still. Smaller values give a smoother, more detailed trail but record more data and use more battery.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">
                  Minimum Distance
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={trackingDistance}
                    onChange={(e) => setTrackingDistance(e.target.value)}
                    className="w-full px-3 py-2.5 pr-16 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-theme-secondary">meters</span>
                </div>
                <p className="text-xs text-theme-secondary mt-1">1 - 500 m (default 8)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">
                  Minimum Interval
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="2"
                    max="600"
                    value={trackingInterval}
                    onChange={(e) => setTrackingInterval(e.target.value)}
                    className="w-full px-3 py-2.5 pr-16 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-theme-secondary">seconds</span>
                </div>
                <p className="text-xs text-theme-secondary mt-1">2 - 600 s (default 12)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">
                  Leave-Property Radius
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="50"
                    max="5000"
                    value={trackingGeofence}
                    onChange={(e) => setTrackingGeofence(e.target.value)}
                    className="w-full px-3 py-2.5 pr-16 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-theme-secondary">meters</span>
                </div>
                <p className="text-xs text-theme-secondary mt-1">50 - 5000 m (default 500). Tracking stops once a tech is this far from the property center.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">
                  Maximum Accuracy
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={trackingMaxAccuracy}
                    onChange={(e) => setTrackingMaxAccuracy(e.target.value)}
                    className="w-full px-3 py-2.5 pr-16 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-theme-secondary">meters</span>
                </div>
                <p className="text-xs text-theme-secondary mt-1">10 - 200 m (default 35). GPS fixes less accurate than this are not recorded, keeping the path trail clean.</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleSaveTrackingSettings}
                disabled={savingTracking}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {savingTracking ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                {savingTracking ? 'Saving...' : 'Save Settings'}
              </button>
              {trackingSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle size={14} />
                  Saved
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="px-6 py-4 border-b border-line bg-subtle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
              <ScrollText size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-theme-primary">Reports</h3>
              <p className="text-xs text-theme-secondary">What appears on printed and emailed reports</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <h4 className="text-sm font-semibold text-theme-primary">Water-restriction violations in reports</h4>
          <p className="text-xs text-theme-secondary mb-3">
            When enabled, controllers whose programmed schedule breaks the property's water-restriction
            policy are flagged with a warning on printed and emailed reports. The on-screen manager
            review always shows these violations regardless of this setting. On by default.
          </p>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={showWaterViolations}
              disabled={savingWaterViolations}
              onClick={() => void handleToggleWaterViolations(!showWaterViolations)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                showWaterViolations ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition-transform ${
                  showWaterViolations ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-theme-primary">{showWaterViolations ? 'Shown on reports' : 'Hidden from reports'}</span>
            {savingWaterViolations && <Loader size={14} className="animate-spin text-theme-secondary" />}
          </label>
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="px-6 py-4 border-b border-line bg-subtle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Clock size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-theme-primary">Inspection Duration</h3>
              <p className="text-xs text-theme-secondary">Time-on-site shown on the manager review</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* How it works — admin documentation */}
          <div className="flex items-start gap-2 rounded-lg bg-indigo-50 border border-indigo-100 dark:bg-indigo-500/10 dark:border-indigo-500/30 p-3 text-xs text-indigo-900 dark:text-indigo-200">
            <Info size={16} className="mt-0.5 shrink-0 text-indigo-500" />
            <div className="space-y-1.5">
              <p className="font-semibold">How inspection duration is calculated</p>
              <p>
                As a technician works an inspection, the app records timestamped activity (form saves,
                GPS captures, and location heartbeats) tied to that report. The duration is built from
                those timestamps — it is <span className="font-semibold">not</span> a manual timer.
              </p>
              <p>
                Because an inspection can be done over several visits (a tech starts, drives away, and
                comes back — sometimes on a different day), the app does not simply subtract the first
                timestamp from the last (that would count hours of driving and overnight gaps). Instead,
                it groups the activity into <span className="font-semibold">visits</span>: whenever there
                is a gap longer than the <span className="font-semibold">visit-gap</span> below, a new
                visit starts. The reported time is the <span className="font-semibold">sum of each
                visit's own length</span>, so idle time between visits is excluded.
              </p>
              <p>
                It is shown on the manager review as <span className="font-semibold">“Time on site”</span>
                with the number of visits (e.g. <span className="italic">“1h 45m · 2 visits”</span>). It is
                visible to managers and admins only — never to the technician, and never on the printed or
                emailed report.
              </p>
            </div>
          </div>

          {/* Enable toggle */}
          <div>
            <h4 className="text-sm font-semibold text-theme-primary">Show inspection duration</h4>
            <p className="text-xs text-theme-secondary mb-3">When off, the time-on-site field is hidden from the manager review. On by default.</p>
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                role="switch"
                aria-checked={durationEnabled}
                disabled={savingDurationEnabled}
                onClick={() => void handleToggleDurationEnabled(!durationEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                  durationEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition-transform ${
                    durationEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-theme-primary">{durationEnabled ? 'Enabled' : 'Disabled'}</span>
              {savingDurationEnabled && <Loader size={14} className="animate-spin text-theme-secondary" />}
            </label>
          </div>

          {/* Visit-gap threshold */}
          <div className="border-t border-line pt-4">
            <label className="block text-sm font-semibold text-theme-primary">Visit-gap (minutes)</label>
            <p className="text-xs text-theme-secondary mb-2">
              A gap in activity longer than this starts a new visit. 1–240 minutes (default 15). Lower values
              split a single visit on short breaks; higher values merge return trips into one visit.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={240}
                value={durationGapMinutes}
                onChange={(e) => setDurationGapMinutes(e.target.value)}
                className="w-28 px-3 py-2 border border-line bg-surface text-theme-primary rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <span className="text-xs text-theme-secondary">minutes</span>
              <button
                type="button"
                onClick={handleSaveDurationGap}
                disabled={savingDurationGap}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {savingDurationGap ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                {savingDurationGap ? 'Saving...' : 'Save'}
              </button>
              {durationGapSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle size={14} /> Saved
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="px-6 py-4 border-b border-line bg-subtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-600 flex items-center justify-center">
                <Mail size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-primary">Azure Email</h3>
                <p className="text-xs text-theme-secondary">Communication Services for report delivery</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {azureConfigured ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {t('integrations.configured', 'Configured')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-subtle text-theme-secondary">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  {t('integrations.notConfigured', 'Not Configured')}
                </span>
              )}
              <button
                onClick={() => setLogViewer({ name: 'azure_email', label: 'Azure Email' })}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 transition-colors"
              >
                <ScrollText size={12} />
                {t('integrations.viewLogs', 'View Logs')}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-theme-primary mb-1.5">
              Connection String
            </label>
            <div className="relative">
              <input
                type={showConnectionString ? 'text' : 'password'}
                value={azureConnectionString}
                onChange={(e) => setAzureConnectionString(e.target.value)}
                placeholder="endpoint=https://your-resource.communication.azure.com/;accesskey=..."
                className="w-full px-3 py-2.5 pr-10 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowConnectionString(!showConnectionString)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-theme-secondary hover:text-theme-primary"
              >
                {showConnectionString ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-theme-secondary mt-1.5">
              Found in your Azure Communication Services resource under Keys. Contains the endpoint and access key.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-primary mb-1.5">
              Sender Address
            </label>
            <input
              type="email"
              value={azureSender}
              onChange={(e) => setAzureSender(e.target.value)}
              placeholder="DoNotReply@yourdomain.com"
              className="w-full px-3 py-2.5 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
            />
            <p className="text-xs text-theme-secondary mt-1.5">
              A verified sender address from your Azure Email Communication Service domain.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSaveAzure}
              disabled={savingAzure}
              className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {savingAzure ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              {savingAzure ? 'Saving...' : 'Save Settings'}
            </button>

            <button
              onClick={handleTestAzure}
              disabled={testingAzure || !azureConfigured}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {testingAzure ? <Loader size={16} className="animate-spin" /> : <Wifi size={16} />}
              {testingAzure ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          {azureTestResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              azureTestResult.success
                ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30'
                : 'bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30'
            }`}>
              {azureTestResult.success
                ? <CheckCircle size={20} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                : <XCircle size={20} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-sm font-medium ${azureTestResult.success ? 'text-green-800 dark:text-green-300' : 'text-amber-800 dark:text-amber-300'}`}>
                  {azureTestResult.success ? 'Test Successful' : 'Note'}
                </p>
                <p className={`text-xs mt-0.5 ${azureTestResult.success ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'}`}>
                  {azureTestResult.message}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={errorModal !== null}
        title={errorModal?.title ?? ''}
        message={errorModal?.message ?? ''}
        variant="warning"
        confirmText="OK"
        hideCancel
        onConfirm={() => setErrorModal(null)}
        onCancel={() => setErrorModal(null)}
        errorContext={errorModal?.error ? {
          error: errorModal.error,
          componentName: 'IntegrationSettings',
          action: errorModal.action,
          pageContext: 'integrations',
        } : undefined}
      />

      <ConfirmModal
        isOpen={confirmDialog !== null}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        confirmText="Continue"
        cancelText="Cancel"
        variant="info"
        onConfirm={() => confirmDialog?.onConfirm()}
        onCancel={() => setConfirmDialog(null)}
      />

      <IntegrationLogViewer
        isOpen={logViewer !== null}
        onClose={() => setLogViewer(null)}
        integrationName={logViewer?.name ?? ''}
        integrationLabel={logViewer?.label ?? ''}
      />
    </div>
  );
}
